import React, { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Upload, FileText, X, User, Camera } from "lucide-react";
import { toast } from "sonner";

const MAX_SIZE = 20 * 1024 * 1024;

const uploadSingle = async (file, category) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  fd.append("visibility", "private");
  const res = await api.post("/files/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/**
 * Avatar uploader with circular preview.
 * Calls `onChange({ url, name, id })` on successful upload.
 */
export const AvatarUpload = ({ value, name, onChange, size = 96 }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("cxoora_token") || "";
  const previewUrl = value?.download_url
    ? `${API_BASE}${value.download_url}?auth=${encodeURIComponent(token)}&inline=true`
    : value?.url || null;
  const initials = (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleFile = async (file) => {
    if (!file) return;
    if (!/image\/(png|jpe?g|webp)/.test(file.type) && !/\.(png|jpe?g|webp)$/i.test(file.name)) {
      toast.error("Formato non supportato. Usa PNG o JPG.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Foto troppo grande (max 5 MB).");
      return;
    }
    setBusy(true);
    try {
      const data = await uploadSingle(file, "avatar");
      onChange && onChange(data);
      toast.success("Foto caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4" data-testid="avatar-upload">
      <div
        className="relative rounded-full overflow-hidden border-2 border-[#2D6BFF]/15 bg-gradient-to-br from-[#EAF0FF] to-white flex items-center justify-center cursor-pointer hover:border-[#2D6BFF] transition"
        style={{ width: size, height: size }}
        onClick={() => inputRef.current?.click()}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="Profilo" className="w-full h-full object-cover" />
        ) : (
          <span className="font-display text-2xl font-bold text-[#2D6BFF]">{initials || <User size={28} />}</span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="animate-spin text-[#2D6BFF]" size={20} />
          </div>
        )}
        <span className="absolute bottom-0 right-0 bg-[#2D6BFF] text-white rounded-full p-1.5 shadow-md">
          <Camera size={12} />
        </span>
      </div>
      <div>
        <p className="text-sm font-medium text-slate-900">Foto profilo</p>
        <p className="text-xs text-slate-500">PNG / JPG, max 5 MB. Consigliata: quadrata 400×400 px.</p>
        <div className="mt-2 flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="avatar-upload-btn">
            {value ? "Cambia foto" : "Carica foto"}
          </Button>
          {value && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)} className="text-red-600 hover:text-red-700" data-testid="avatar-remove-btn">
              Rimuovi
            </Button>
          )}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="avatar-file-input"
      />
    </div>
  );
};

/**
 * CV uploader. PDF / DOCX. Shows filename + download link if already uploaded.
 * Calls `onChange({ url, name, id })`.
 */
export const CvUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const token = localStorage.getItem("cxoora_token") || "";

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      toast.error("CV troppo grande (max 20 MB).");
      return;
    }
    if (!/\.(pdf|docx|doc)$/i.test(file.name)) {
      toast.error("Formato non supportato. Usa PDF o DOCX.");
      return;
    }
    setBusy(true);
    try {
      const data = await uploadSingle(file, "cv");
      onChange && onChange(data);
      toast.success("CV caricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="cv-upload">
      {value ? (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
          <FileText size={18} className="text-[#2D6BFF]" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{value.name || "CV"}</p>
            {value.size && <p className="text-xs text-slate-500">{(value.size / 1024 / 1024).toFixed(2)} MB</p>}
          </div>
          {value.download_url && (
            <a
              href={`${API_BASE}${value.download_url}?auth=${encodeURIComponent(token)}&inline=true`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-[#2D6BFF] hover:underline"
              data-testid="cv-preview-link"
            >
              Visualizza
            </a>
          )}
          <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="cv-replace-btn">
            Sostituisci
          </Button>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-red-500 hover:text-red-700"
            aria-label="Rimuovi CV"
            data-testid="cv-remove-btn"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          className="cursor-pointer cxo-card border-2 border-dashed border-slate-300 hover:border-[#2D6BFF] hover:bg-[#EAF0FF]/30 transition-colors p-5 text-center"
          data-testid="cv-dropzone"
        >
          <Upload size={20} className="mx-auto text-slate-400" />
          <p className="mt-1.5 text-sm font-medium text-slate-700">Carica il tuo CV (PDF / DOCX)</p>
          <p className="text-xs text-slate-500">Opzionale ma fortemente consigliato. Max 20 MB.</p>
          {busy && <Loader2 className="animate-spin text-[#2D6BFF] mx-auto mt-2" size={18} />}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.doc"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
        data-testid="cv-file-input"
      />
    </div>
  );
};
